<script setup>
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { ArrowRight, Check, Copy, FileText, ShieldCheck, Sparkles, Upload, Zap } from 'lucide-vue-next'
import { usePageSeo, faqJsonLd } from '@/composables/usePageSeo'

// HowTo (the connect flow) + FAQPage (troubleshooting) are the two schema types
// answer engines lean on for "how do I connect X to Claude" queries — this page
// is literally both, so emit both.
const howToJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Connect Papersnap to Claude (MCP)',
  description:
    'Add Papersnap as an MCP connector in Claude to extract structured data from documents inside the chat.',
  totalTime: 'PT2M',
  step: [
    { '@type': 'HowToStep', position: 1, name: 'Open connector settings', text: "In Claude, go to Settings → Connectors → Add custom connector." },
    { '@type': 'HowToStep', position: 2, name: 'Paste the server URL', text: 'Paste the Papersnap MCP server URL and leave the OAuth client fields empty — registration is automatic.' },
    { '@type': 'HowToStep', position: 3, name: 'Sign in with Papersnap', text: 'Click Connect and sign in with your Papersnap account (a free account works).' },
    { '@type': 'HowToStep', position: 4, name: 'Set tools to Always allow', text: 'Set the Papersnap tools to Always allow, then ask Claude to extract data from a document.' },
  ],
}

const faqs = [
  { question: 'Which AI assistants can use Papersnap?', answer: 'Any client that speaks the open Model Context Protocol (MCP): Claude (web and desktop), ChatGPT developer mode, Cursor, agent frameworks, and AWS Bedrock agent stacks. Point the client at the Papersnap MCP server URL and sign in with your Papersnap account.' },
  { question: 'Does using Papersnap through an AI agent cost extra?', answer: 'No. Extractions use the same page quota as the Papersnap web app. The free tier includes pages every month with no credit card, and failed documents are never billed.' },
  { question: 'How do I upload a local file from Claude Desktop?', answer: 'Web-hosted document URLs work out of the box. For local files in Claude Desktop, allow the Papersnap upload host in Settings → Capabilities → Allow network egress, then your assistant can upload the file directly.' },
  { question: 'Why do the tools look outdated or missing?', answer: 'MCP clients pin the tool list when you first connect. Remove and re-add the connector to refresh it.' },
  { question: 'Is my document data private?', answer: 'Documents auto-delete after 30 days, are never used to train AI models, and are isolated per account behind your sign-in. You can delete any document at any time from your dashboard.' },
]

usePageSeo({
  title: 'MCP — Use Papersnap inside Claude & AI Agents',
  description:
    'Connect Papersnap to Claude, ChatGPT, and any MCP-compatible AI agent. Extract structured data from invoices, receipts, and statements right inside the chat — metered on your Papersnap plan.',
  path: '/mcp',
  jsonLd: [howToJsonLd, faqJsonLd(faqs)],
})

// Branded CloudFront front over the MCP server's Lambda Function URL.
const MCP_URL = 'https://mcp.keelara.com'
const CONSOLE_URL = 'https://console.keelara.com'
// Claude Desktop's code sandbox needs this host allowed for local-file uploads.
// Branded CloudFront front over the S3 upload bucket.
const EGRESS_DOMAIN = 'uploads.keelara.com'

const copied = ref('')
async function copy(text, key) {
  try {
    await navigator.clipboard.writeText(text)
    copied.value = key
    setTimeout(() => { if (copied.value === key) copied.value = '' }, 2000)
  } catch { /* clipboard unavailable — user can select manually */ }
}

const TOOLS = [
  {
    icon: FileText,
    name: 'papersnap_extract_document',
    what: 'Extract structured data from a web-hosted document (https URL) — fields, line items, and tables with confidence scores, as JSON or CSV.',
    pages: 'Uses plan pages',
  },
  {
    icon: Upload,
    name: 'papersnap_request_upload',
    what: 'Get a secure upload URL for a local or attached file (up to 25 MB). Your assistant uploads the raw file directly — extraction starts automatically.',
    pages: 'Uses plan pages',
  },
  {
    icon: Zap,
    name: 'papersnap_get_result',
    what: 'Fetch the extraction result for a submitted document. Waits server-side until processing completes — no polling loops.',
    pages: 'Free',
  },
  {
    icon: Sparkles,
    name: 'papersnap_list_recent',
    what: "List your account's recent documents with status and type.",
    pages: 'Free',
  },
]
</script>

<template>
  <main class="pb-20">
    <!-- Hero -->
    <section class="container-app pt-14 pb-10 text-center">
      <p class="mb-3 text-xs font-semibold uppercase tracking-widest text-primary">Papersnap MCP</p>
      <h1 class="mx-auto max-w-3xl font-display text-4xl font-bold tracking-tight sm:text-5xl">
        Use Papersnap inside Claude — and any AI agent.
      </h1>
      <p class="mx-auto mt-4 max-w-2xl text-lg text-slate-600 dark:text-slate-400">
        Papersnap is available as an <strong>MCP connector</strong>: drop an invoice, receipt, or statement into your AI
        assistant and get clean, confidence-scored data back — extracted by Papersnap, metered on your plan,
        mirrored in your dashboard.
      </p>
      <div class="mt-6 flex flex-wrap items-center justify-center gap-3">
        <RouterLink to="/signup" class="btn-primary">Create a free account <ArrowRight class="h-4 w-4" /></RouterLink>
        <a href="#connect" class="btn-secondary">Connect instructions</a>
      </div>
      <p class="mt-3 text-xs text-slate-500">You'll sign in with your Papersnap account during the connect step — free tier included.</p>
    </section>

    <!-- Connect steps -->
    <section id="connect" class="container-app py-10">
      <h2 class="font-display text-2xl font-semibold">Connect in Claude (2 minutes)</h2>
      <ol class="mt-6 space-y-5">
        <li class="card-surface flex gap-4 p-5">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">1</span>
          <div class="min-w-0">
            <p class="font-medium">Open Claude's connector settings</p>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              In Claude (web or desktop): <span class="font-mono text-xs">Settings → Connectors → Add custom connector</span>.
              Available on Claude paid plans.
            </p>
          </div>
        </li>
        <li class="card-surface flex gap-4 p-5">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">2</span>
          <div class="min-w-0 flex-1">
            <p class="font-medium">Paste the Papersnap MCP server URL</p>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">Name it anything (e.g. "Papersnap"). Leave the OAuth Client ID/Secret fields empty — registration is automatic.</p>
            <div class="mt-3 flex items-center gap-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
              <code class="min-w-0 flex-1 truncate px-3 py-2 font-mono text-xs text-slate-800 dark:text-slate-100">{{ MCP_URL }}</code>
              <button type="button" class="btn-ghost shrink-0" @click="copy(MCP_URL, 'url')">
                <Check v-if="copied === 'url'" class="h-4 w-4 text-primary" />
                <Copy v-else class="h-4 w-4" />
                <span class="hidden sm:inline">{{ copied === 'url' ? 'Copied' : 'Copy' }}</span>
              </button>
            </div>
          </div>
        </li>
        <li class="card-surface flex gap-4 p-5">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">3</span>
          <div class="min-w-0">
            <p class="font-medium">Click Connect and sign in with your Papersnap account</p>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              A Papersnap login window opens. Sign in (or
              <RouterLink to="/signup" class="text-primary hover:underline">create a free account</RouterLink>)
              and approve access. Your extractions, quota, and history are shared with the web app — one account, two front doors.
            </p>
          </div>
        </li>
        <li class="card-surface flex gap-4 p-5">
          <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-semibold text-primary">4</span>
          <div class="min-w-0">
            <p class="font-medium">Recommended: set the tools to "Always allow"</p>
            <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
              In the connector's tool permissions, choose <em>Always allow</em> so extractions run without a
              confirmation prompt on every call. Then try it:
              <span class="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs dark:bg-slate-800">"Use Papersnap to extract the data from this document."</span>
            </p>
          </div>
        </li>
      </ol>
    </section>

    <!-- Local files / egress -->
    <section class="container-app py-10">
      <div class="card-surface p-6">
        <h2 class="font-display text-xl font-semibold">Uploading local files from Claude Desktop</h2>
        <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Web-hosted documents (a URL) work out of the box. To let Claude <strong>Desktop</strong> upload files from
          your computer, allow Papersnap's upload host in the sandbox network settings — a one-time step:
        </p>
        <ol class="mt-4 list-decimal space-y-2 pl-5 text-sm text-slate-600 dark:text-slate-400">
          <li>Claude Desktop → <span class="font-mono text-xs">Settings → Capabilities → Allow network egress</span></li>
          <li>Under <em>Additional allowed domains</em>, add:</li>
        </ol>
        <div class="mt-3 flex items-center gap-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
          <code class="min-w-0 flex-1 truncate px-3 py-2 font-mono text-xs">{{ EGRESS_DOMAIN }}</code>
          <button type="button" class="btn-ghost shrink-0" @click="copy(EGRESS_DOMAIN, 'egress')">
            <Check v-if="copied === 'egress'" class="h-4 w-4 text-primary" />
            <Copy v-else class="h-4 w-4" />
            <span class="hidden sm:inline">{{ copied === 'egress' ? 'Copied' : 'Copy' }}</span>
          </button>
        </div>
        <p class="mt-3 text-xs text-slate-500">
          This is the exact host your uploads go to — nothing broader. Uploads are direct and byte-exact; your
          assistant never re-encodes or degrades the file.
        </p>
      </div>
    </section>

    <!-- Tools -->
    <section class="container-app py-10">
      <h2 class="font-display text-2xl font-semibold">What your assistant can do</h2>
      <div class="mt-6 grid gap-4 sm:grid-cols-2">
        <div v-for="t in TOOLS" :key="t.name" class="card-surface p-5">
          <div class="flex items-center gap-3">
            <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <component :is="t.icon" class="h-4 w-4" />
            </span>
            <code class="min-w-0 truncate font-mono text-xs font-semibold">{{ t.name }}</code>
          </div>
          <p class="mt-3 text-sm text-slate-600 dark:text-slate-400">{{ t.what }}</p>
          <div class="mt-3 flex items-center justify-between gap-2">
            <span class="text-xs font-medium" :class="t.pages === 'Free' ? 'text-primary' : 'text-slate-500'">{{ t.pages }}</span>
            <a :href="`${CONSOLE_URL}/?try=${t.name}`" target="_blank" rel="noopener" class="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
              Open in Playground
              <svg viewBox="0 0 20 20" fill="none" class="h-3.5 w-3.5"><path d="M5 10h9m0 0l-3.5-3.5M14 10l-3.5 3.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
            </a>
          </div>
        </div>
      </div>
      <p class="mt-4 text-sm text-slate-600 dark:text-slate-400">
        And because your assistant reasons over the results, whole workflows come free:
        <em>"extract these receipts and total them by vendor"</em>, <em>"which line items look wrong?"</em>,
        <em>"turn this statement into CSV."</em>
      </p>
    </section>

    <!-- Billing + privacy -->
    <section class="container-app grid gap-4 py-10 lg:grid-cols-2">
      <div class="card-surface p-6">
        <h2 class="font-display text-xl font-semibold">How usage works</h2>
        <ul class="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-400">
          <li class="flex gap-2"><Check class="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Extractions use the <strong>same page quota</strong> as the web app — Free includes pages every month, no card required.</li>
          <li class="flex gap-2"><Check class="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Your assistant shows a running "pages used" count and tells you when you're close to the limit.</li>
          <li class="flex gap-2"><Check class="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Failed documents are <strong>never billed</strong>.</li>
          <li class="flex gap-2"><Check class="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Need more pages? <RouterLink to="/pricing" class="text-primary hover:underline">Plans start at $9/mo for 200 pages</RouterLink>.</li>
        </ul>
      </div>
      <div class="card-surface p-6">
        <h2 class="flex items-center gap-2 font-display text-xl font-semibold"><ShieldCheck class="h-5 w-5 text-primary" /> Privacy</h2>
        <ul class="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-400">
          <li class="flex gap-2"><Check class="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Documents auto-delete after 30 days, and you can delete any document at any time from your dashboard.</li>
          <li class="flex gap-2"><Check class="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Your documents are <strong>never used to train AI models</strong>.</li>
          <li class="flex gap-2"><Check class="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Uploads are isolated per account; access requires your sign-in. <RouterLink to="/privacy" class="text-primary hover:underline">Privacy policy</RouterLink>.</li>
        </ul>
      </div>
    </section>

    <!-- Other clients + troubleshooting -->
    <section class="container-app py-10">
      <div class="card-surface p-6">
        <h2 class="font-display text-xl font-semibold">Beyond Claude</h2>
        <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
          Papersnap MCP follows the open <strong>Model Context Protocol</strong>, so the same URL works in any
          MCP-compatible client — ChatGPT (Apps/developer mode), Cursor, agent frameworks, and AWS Bedrock agent
          stacks. Point the client at the server URL above and authenticate with your Papersnap account.
        </p>
        <h3 class="mt-6 font-display text-base font-semibold">Troubleshooting</h3>
        <ul class="mt-2 space-y-2 text-sm text-slate-600 dark:text-slate-400">
          <li><strong>Tools look outdated or missing?</strong> Remove and re-add the connector — clients pin the tool list when you first connect.</li>
          <li><strong>Local upload blocked ("host not allowed")?</strong> Add the S3 domain above to Claude Desktop's network egress allowlist.</li>
          <li><strong>"Quota exceeded"?</strong> You've used your plan's pages this month — <RouterLink to="/pricing" class="text-primary hover:underline">upgrade</RouterLink> or wait for the monthly reset.</li>
        </ul>
      </div>
    </section>

    <!-- CTA -->
    <section class="container-app pt-6 text-center">
      <h2 class="font-display text-2xl font-semibold">Snap a document — without leaving the chat.</h2>
      <div class="mt-5 flex flex-wrap items-center justify-center gap-3">
        <RouterLink to="/signup" class="btn-primary">Get started free <ArrowRight class="h-4 w-4" /></RouterLink>
        <button type="button" class="btn-secondary" @click="copy(MCP_URL, 'cta')">
          <Check v-if="copied === 'cta'" class="h-4 w-4 text-primary" />
          <Copy v-else class="h-4 w-4" />
          Copy server URL
        </button>
      </div>
    </section>
  </main>
</template>
